import { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import { ExternalLink, Loader2, AlertCircle, ArrowLeft, Github, MapPin, Building, Users } from "lucide-react";
import { lookupGitHub, type GitHubResult } from "@/lib/api";
import { IMG_BASE, POPULAR_GITHUB } from "@/lib/constants";
import { PageLayout } from "@/components/layout/page-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";

const AVATAR_FALLBACK_QUERY = "&fallback=boring";
const AVATAR_VARIANT_EXAMPLE = "boring-abstract";

interface GitHubPageProps {
  serverData?: GitHubResult | null;
}

export default function GitHubPage({ serverData }: GitHubPageProps) {
  const { username } = useParams<{ username: string }>();
  const [user, setUser] = useState<GitHubResult | null>(serverData ?? null);
  const [loading, setLoading] = useState(!serverData);

  useEffect(() => {
    if (serverData) return;
    if (!username) return;
    setLoading(true);
    setUser(null);
    lookupGitHub(username)
      .then(setUser)
      .catch(() => setUser({ error: "Failed to load GitHub data" } as GitHubResult))
      .finally(() => setLoading(false));
  }, [username, serverData]);

  if (loading) {
    return (
      <div className="py-32 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
        <p className="text-sm text-muted-foreground">Loading GitHub profile...</p>
      </div>
    );
  }

  if (!user || user.error) {
    return (
      <div className="py-32 text-center">
        <div className="max-w-md mx-auto px-6">
          <AlertCircle className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">GitHub user not found</h1>
          <p className="text-sm text-secondary-foreground mb-6">
            {user?.error || `We couldn't find "${username}" on GitHub.`}
          </p>
          <Button asChild>
            <Link to="/"><ArrowLeft className="w-4 h-4" />Back to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PageLayout breadcrumbs={[{ label: "GitHub", to: "/" }, { label: user.username || username! }]}>
      {/* Hero */}
      <section className="mb-12">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="shrink-0">
            <img
              src={`${IMG_BASE}/github/${username}?size=256${AVATAR_FALLBACK_QUERY}`}
              alt={`${username} GitHub avatar`}
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-contain bg-white border shadow-sm"
              onError={(e) => { (e.target as HTMLElement).style.opacity = "0.3"; }}
            />
          </div>
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
              {user.name || username}
            </h1>
            {user.name && (
              <p className="text-lg text-muted-foreground mb-2">@{user.username}</p>
            )}
            {user.bio && (
              <p className="text-base text-secondary-foreground leading-relaxed mb-3 max-w-2xl">{user.bio}</p>
            )}
            <div className="flex gap-2 flex-wrap mb-3">
              <Badge variant="secondary">{user.type}</Badge>
              {user.company && (
                <Badge variant="outline"><Building className="w-3 h-3 mr-1" />{user.company}</Badge>
              )}
              {user.location && (
                <Badge variant="outline"><MapPin className="w-3 h-3 mr-1" />{user.location}</Badge>
              )}
            </div>
            <div className="flex gap-4 text-sm text-muted-foreground">
              <span><Users className="w-3.5 h-3.5 inline mr-1" /><strong className="text-foreground">{user.followers.toLocaleString()}</strong> followers</span>
              <span><strong className="text-foreground">{user.repos}</strong> repos</span>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Usage */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">Quick Usage</h2>
        <CodeBlock language="markup" code={`<img src="${IMG_BASE}/github/${username}" />\n<img src="${IMG_BASE}/github/${username}?fallback=${AVATAR_VARIANT_EXAMPLE}" />`} />
      </section>

      {/* Links */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">Links</h2>
        <div className="flex gap-2 flex-wrap">
          <a href={`https://github.com/${username}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-card border rounded-xl text-sm font-medium text-secondary-foreground hover:text-foreground hover:border-border-strong hover:shadow-sm transition-all no-underline">
            <Github className="w-4 h-4" /> GitHub Profile <ExternalLink className="w-3 h-3 text-muted-foreground" />
          </a>
          {user.blog && (
            <a href={user.blog.startsWith("http") ? user.blog : `https://${user.blog}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-card border rounded-xl text-sm font-medium text-secondary-foreground hover:text-foreground hover:border-border-strong hover:shadow-sm transition-all no-underline">
              Website <ExternalLink className="w-3 h-3 text-muted-foreground" />
            </a>
          )}
        </div>
      </section>

      {/* API Usage */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">API Usage</h2>
        <CodeBlock language="markup" code={`<!-- GitHub avatar image -->\n<img src="${IMG_BASE}/github/${username}" />\n\n<!-- Variant-specific avatar fallback -->\n<img src="${IMG_BASE}/github/${username}?size=256&fallback=${AVATAR_VARIANT_EXAMPLE}" />\n\n<!-- GitHub profile data (JSON) -->\n<script>\nfetch("https://api.loadlogo.com/github/${username}")\n</script>`} />
      </section>

      {/* Related GitHub Users */}
      <section>
        <h2 className="text-lg font-bold tracking-tight mb-4">Popular GitHub Users</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {POPULAR_GITHUB.filter((u) => u !== username).slice(0, 8).map((ghUser) => (
            <Link key={ghUser} to={`/github/${ghUser}`} className="flex items-center gap-3 p-3 bg-card border rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all no-underline">
              <img src={`${IMG_BASE}/github/${ghUser}?size=64${AVATAR_FALLBACK_QUERY}`} alt={ghUser} className="w-8 h-8 rounded-lg object-contain" loading="lazy" />
              <span className="text-sm font-medium text-secondary-foreground truncate">{ghUser}</span>
            </Link>
          ))}
        </div>
      </section>
    </PageLayout>
  );
}
